import { useEffect, useState, type FormEvent } from 'react';
import {
  AlertBanner,
  Button,
  ConsentCheckbox,
  Input,
  LoadingState,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import {
  endpoints,
  ApiError,
  type InvitationPreview,
  type PrivacyNoticeEntry,
} from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * Layar Terima Undangan — M01 · rute `/accept-invitation` · izin PUBLIC.
 *
 * LAYAR INI MEMBERI TAHU, LALU MEMINTA PENGAKUAN TELAH DIBERI TAHU. Ia tidak
 * meminta izin, dan bedanya bukan tata bahasa.
 *
 * Bentuk sebelumnya meminta persetujuan atas seluruh pemrosesan — termasuk
 * pemrosesan untuk menjalankan hubungan kerja dan memenuhi kewajiban K3, yang tetap
 * akan berjalan apa pun jawabannya. Sebuah izin yang penolakannya tidak dapat
 * dihormati bukan izin; ia teater persetujuan, dan catatan yang dihasilkannya justru
 * berbahaya karena ia terlihat seperti bukti.
 *
 * Yang sesungguhnya berlaku: organisasi tempat orang ini bekerja adalah PENGENDALI
 * Data Pribadi, dan ia yang menetapkan dasar pemrosesan di dalam hubungan kerjanya
 * lewat perjanjian pemrosesan data di tingkat organisasi (Pasal 51). PQM adalah
 * PROSESOR. Yang TIDAK dapat ditandatangani perusahaan atas nama orangnya adalah hak
 * orang itu untuk DIBERI TAHU (Pasal 21) — karena itu layar ini tetap ada, kotak
 * centangnya tetap ada, dan tetap tidak tercentang.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * TIGA CACAT UAT G3 YANG SELURUHNYA ADA DI LAYAR INI, DAN APA YANG BERUBAH
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * **U2-04 (High) — nama Pengendali tidak pernah muncul; sebuah KALIMAT disisipkan ke
 * tempat nama.** `privacy_notice.controller_name` berisi *"Organisasi tempat Anda
 * bekerja adalah pengendali data pribadi. PQM memproses data ini atas instruksi
 * organisasi Anda."* — sebuah kalimat, bukan nama — dan layar ini menyisipkannya TIGA
 * KALI ke `{organisation}`. Yang terbaca di layar, apa adanya:
 *
 * > *"The organisation you work for is the data controller. PQM processes this data on
 * > that organisation's instructions. decides how your personal data is used here. …
 * > Questions about your data go to The organisation you work for is the data
 * > controller. … first — ask your manager or its human resources team."*
 *
 * Perbaikannya di TIGA lapis, dan ketiganya diperlukan:
 *   · migrasi 0034 memisahkan `controller_statement` (kalimatnya) dari
 *     `controller_name` (nama, atau NULL), beserta CHECK yang menolak kalimat masuk
 *     kembali ke tempat nama;
 *   · `GET /v1/auth/invitations/{token}` memberi NAMA organisasinya — dari undangannya,
 *     tempat ia benar-benar diketahui. Ia tidak dapat datang dari naskah
 *     pemberitahuan, karena satu naskah berlaku bagi semua organisasi;
 *   · layar ini memakai nama itu, dan ketika ia belum termuat memakai kalimat netral
 *     `accept.controllerUnknown` — BUKAN string kosong, yang menghasilkan kalimat tanpa
 *     subjek (cacat yang sama masih hidup di `/settings/privacy` sebelum perbaikan ini).
 *
 * **U2-05 (High) — `{organisation}` tampil MENTAH.** Paragraf "apa yang dilakukan
 * centang ini, dan apa yang tidak" memanggil `t()` TANPA parameter, sementara
 * kalimatnya menuntut `{organisation}`; perender ICU melempar, dan `createTranslator()`
 * mengembalikan pesan MENTAH supaya layar tidak jatuh. Sekarang parameternya diberikan —
 * dan gerbang `tools/ci/cek-teks-antarmuka.py` bagian 6 menggagalkan build bila sebuah
 * pemanggilan `t()` kekurangan placeholder yang dituntut katalognya, di KEDUA bahasa.
 *
 * **U2-06 (High) — pengalih bahasa hanya menerjemahkan SEBAGIAN layar.** Bentuk
 * sebelumnya menyimpan `const [shown, setShown] = useState(locale)` dan tombolnya hanya
 * mengganti state itu: naskah pemberitahuan berpindah ke bahasa Indonesia sementara
 * label kotak centang, paragraf akibat, label kata sandi, dan tombol kirim tetap bahasa
 * Inggris — pada layar yang memutuskan apakah seseorang sudah diberi tahu.
 *
 * Sebabnya struktural: DUA sumber bahasa di satu layar. Sekarang ada SATU — bahasa
 * sesi — dan tombolnya memanggil `setLocale()`, yang menukar perender ICU sekaligus
 * memindahkan naskahnya. Gerbang bagian 7 menolak salinan kedua itu kembali.
 *
 * **U2-23 / D-05 (Medium, ikut tertutup)** — bahasa baku layar ini dulu Inggris,
 * termasuk untuk pekerja yang diundang oleh sebuah pabrik di Indonesia. Bahasa awalnya
 * sekarang diambil dari `default_locale` organisasi yang mengundang, yang diketahui
 * sebelum satu kata pun dirender.
 *
 * LIMA HAL YANG TETAP DITEGAKKAN LAYAR INI
 * ----------------------------------------
 * 1. Pemberitahuan privasi ditampilkan PENUH sebelum kata sandi ditetapkan.
 * 2. Organisasinya DISEBUT sebagai Pengendali, beserta ke mana orang ini bertanya.
 * 3. Kotak centangnya TIDAK tercentang, dan naskahnya menyatakan apa yang dicatat.
 * 4. Tanpa centang, tombolnya TIDAK mati diam-diam — yang muncul pernyataan jujur.
 * 5. Versi pemberitahuan yang DIBACA dikirim bersama pengakuannya.
 */
export function AcceptInvitation({ token }: { token: string }) {
  const { t, translator, locale, setLocale, refresh } = useSession();
  const [notices, setNotices] = useState<PrivacyNoticeEntry[] | null>(null);
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [invitationFailed, setInvitationFailed] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * `perluCentang` MENGGANTIKAN `declined`, dan bedanya bukan penamaan.
   *
   * `declined` menyalakan LAYAR TERSENDIRI yang muncul sesudah orang menekan tombol —
   * artinya ia baru tahu akibatnya setelah salah memilih. Perancang menolak bentuk itu
   * dan menggantinya dengan panel akibat yang SELALU TERLIHAT (`178:1182`, panel "BILA
   * KOTAK INI TIDAK ANDA CENTANG"). `perluCentang` karena itu tidak mengganti layar apa
   * pun: ia hanya menambahkan satu kalimat di bawah panel yang sudah dibaca orangnya, dan
   * memindahkan fokus ke kotaknya.
   */
  const [perluCentang, setPerluCentang] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);

  useEffect(() => {
    let alive = true;
    endpoints
      .privacyNotice(null)
      .then((res) => alive && setNotices(res.notices))
      .catch(() => alive && setNotices([]));
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Undangannya dibaca SEKALI, dan kegagalannya tidak mematikan layar.
   *
   * Sebuah token yang tidak sah dijawab `404 INVITATION_INVALID` — dan pada titik itu
   * yang benar BUKAN menyembunyikan seluruh layar: orang itu sudah berada di sini, dan
   * ia berhak tahu bahwa tautannya sudah terpakai atau kedaluwarsa. Naskah
   * pemberitahuan tetap dapat dibaca (Pasal 21 tidak bergantung pada undangannya), dan
   * tombol kirim yang gagal nanti menjelaskan sisanya.
   */
  useEffect(() => {
    let alive = true;
    endpoints
      .invitationPreview(token)
      .then((res) => {
        if (!alive) return;
        setInvitation(res);
        /**
         * BAHASA AWAL DARI ORGANISASI YANG MENGUNDANG (U2-23 / D-05).
         *
         * Ia disetel hanya SEKALI, saat undangannya termuat, dan hanya bila berbeda:
         * sesudah itu orangnya yang memutuskan lewat tombol pengalih, dan pilihan orang
         * tidak boleh ditimpa oleh data.
         */
        if (res.default_locale === 'id-ID' || res.default_locale === 'en-US') {
          if (res.default_locale !== locale) void setLocale(res.default_locale);
        }
      })
      .catch(() => alive && setInvitationFailed(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * SATU sumber bahasa: `locale` dari sesi. Naskah yang ditampilkan mengikutinya, dan
   * tombol pengalih mengganti sesi — bukan salinan lokal (U2-06).
   */
  const current = notices?.find((n) => n.locale === locale) ?? notices?.[0] ?? null;
  const other = notices?.find((n) => n.locale !== current?.locale) ?? null;
  const mismatch = confirm !== '' && confirm !== password;

  /**
   * NAMA organisasinya, atau kalimat netral — dan tidak pernah string kosong.
   *
   * Urutannya mengikat: nama dari undangan lebih dulu, lalu `controller_name` bila
   * naskahnya memang naskah tenant yang menyebut namanya sendiri, lalu kalimat netral
   * dari katalog. `''` TIDAK ADA di rantai ini: sebuah string kosong menghasilkan
   * "Saya mengerti bahwa  menentukan bagaimana data pribadi saya dipakai di sini" —
   * kalimat tanpa subjek, yang persis cacat yang ditemukan UAT di `/settings/privacy`.
   */
  const organisation =
    invitation?.organisation_name ?? current?.controller_name ?? t('accept.controllerUnknown');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);

    /**
     * Penolakan ditangani DI SINI, sebelum permintaan dikirim, dan itu disengaja:
     * mengirim `notice_acknowledged: false` ke server akan menghasilkan 422 yang
     * benar, tetapi juga akan meninggalkan satu baris jejak yang berbunyi seperti
     * percobaan gagal. Yang sesungguhnya terjadi bukan kegagalan — seseorang belum
     * siap menyatakan bahwa ia sudah membaca, dan itu keadaan yang sah.
     */
    if (!acknowledged) {
      setPerluCentang(true);
      return;
    }
    if (mismatch || password.length < 14) return;

    setBusy(true);
    try {
      await endpoints.acceptInvitation({
        token,
        password,
        notice_acknowledged: true,
        ...(current ? { notice_id: current.id } : {}),
        locale,
      });
      await refresh();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <div className="signin__panel">
        <h1 className="signin__title">{t('accept.title')}</h1>
        {/*
          SIAPA yang mengundang, disebut di baris kedua. Sebelum U2-04 ditutup, nama
          organisasinya tidak pernah muncul di layar ini sama sekali — padahal sistem
          mengetahuinya, dan bilah atas konsol menampilkannya setelah orangnya masuk.
        */}
        <p className="signin__subtitle">{t('accept.subtitle', { organisation })}</p>

        {invitationFailed ? (
          <AlertBanner
            kind="warning"
            signalWord={t('signal.warning')}
            title={t('error.INVITATION_INVALID')}
          />
        ) : null}

        {problem ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem.problem)}
          />
        ) : null}

        <h2 className="signin__subtitle">{t('accept.consentHeading')}</h2>

        {notices === null ? (
          <LoadingState rows={4} label={t('app.loading')} />
        ) : current ? (
          <article className="assist-privacy">
            <p className="assist-privacy__meta">
              {t('privacy.notice.version', { version: current.version })}
            </p>
            {other ? (
              /*
                SATU tombol, DUA akibat, dan itu seluruh isi U2-06: ia mengganti bahasa
                SESI, sehingga naskah pemberitahuan DAN setiap kalimat di sekelilingnya
                berpindah bersama. Bentuk sebelumnya hanya mengganti naskahnya.
              */
              <Button
                variant="text"
                onClick={() => void setLocale(other.locale === 'en-US' ? 'en-US' : 'id-ID')}
              >
                {t('privacy.notice.otherLanguage')}
              </Button>
            ) : null}
            <div className="assist-privacy__body">{current.body}</div>
            {/* Kalimat Pengendali dipakai sebagai KALIMAT, di tempatnya sendiri. */}
            <p className="assist-privacy__meta">{current.controller_statement}</p>
            <p className="assist-privacy__meta">{current.contact}</p>
          </article>
        ) : (
          <AlertBanner
            kind="warning"
            signalWord={t('signal.warning')}
            title={t('privacy.notice.unavailable')}
          />
        )}

        <form className="signin__form" onSubmit={onSubmit} noValidate>
          {/*
            Satu baris yang menyebut PENGENDALINYA dengan NAMA: seseorang yang membaca
            pemberitahuan ini perlu tahu bahwa yang memutuskan bagaimana datanya dipakai
            adalah organisasi tempat ia bekerja — bukan PQM — dan bahwa pertanyaannya
            diajukan ke sana.
          */}
          <p className="assist-privacy__meta">{t('accept.controllerLine', { organisation })}</p>

          <ConsentCheckbox
            label={t('privacy.consent.NOTICE_ACKNOWLEDGED.label', { organisation })}
            /* Parameternya DIBERIKAN — tanpa ini yang tampil adalah `{organisation}` mentah (U2-05). */
            consequence={t('privacy.consent.NOTICE_ACKNOWLEDGED.consequence', { organisation })}
            consequenceHeading={t('accept.withoutConsentHeading')}
            checked={acknowledged}
            onChange={(next) => {
              setAcknowledged(next);
              if (next) setPerluCentang(false);
            }}
          />

          {/*
            PANEL AKIBAT — SELALU TERLIHAT, dan itu yang menggantikan layar penolakan.
            Ia mengatakan TIGA hal sebelum keputusan diambil: akunnya tetap `INVITED` dan
            ia belum bisa masuk; TIDAK ADA yang dihapus dan tautannya tetap berlaku sampai
            tanggal yang disebut; dan ke mana ia bertanya. Tidak ada jalan buntu, karena
            tidak ada pintu yang tertutup di belakangnya.

            Tanggalnya datang dari `GET /v1/auth/invitations/{token}` — bukan dari konstanta
            dan bukan dari naskah: sebuah tanggal yang dikarang layar akan berbeda dari
            tanggal yang ditegakkan basis data, dan yang dibaca orangnya adalah yang di
            layar.
          */}
          <div className="assist-consent__consequence" role="note">
            <p className="assist-consent__consequence-heading">
              {t('accept.ifNotTicked.heading')}
            </p>
            <p className="assist-consent__consequence-body">
              {invitation?.expires_at
                ? t('accept.ifNotTicked.body', {
                    organisation,
                    expires: new Date(invitation.expires_at).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }),
                  })
                : t('accept.ifNotTicked.bodyNoExpiry', { organisation })}
            </p>
            {perluCentang ? (
              <p className="assist-consent__consequence-body" role="alert">
                {t('accept.needsTick')}
              </p>
            ) : null}
          </div>

          <Input
            label={t('accept.password')}
            inputType="password"
            hint={t('accept.password.hint')}
            value={password}
            autoComplete="new-password"
            required
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            label={t('accept.passwordConfirm')}
            inputType="password"
            value={confirm}
            autoComplete="new-password"
            required
            error={mismatch ? t('error.VALIDATION_FAILED') : null}
            onChange={(e) => setConfirm(e.target.value)}
          />

          {/*
            Tombolnya TIDAK dimatikan oleh kotak centang yang kosong. Ia dimatikan
            hanya oleh hal-hal yang memang belum dapat dikirim — kata sandi terlalu
            pendek, atau dua isian yang tidak sama. Belum menyatakan sudah membaca
            bukan kesalahan pengisian; jawabannya berupa penjelasan.

            Panjang minimumnya 14, bukan 12: penguncian akun keras dihapus atas keputusan
            pemilik proyek, dan kebijakan kata sandi adalah kontrol yang menggantikannya
            (`kernels/people/src/password-policy.ts`). Server menegakkan kebijakan
            lengkapnya; yang di sini hanya mencegah permintaan yang pasti ditolak.
          */}
          <Button
            variant="primary"
            htmlType="submit"
            busy={busy}
            fullWidth
            disabled={password.length < 14 || mismatch}
          >
            {t('accept.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
